import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import Company from "../types/company";
import { AuthSession, fetchAuthSession } from "aws-amplify/auth";
import CompanyLicense from "../types/companyLicense";

interface CompanyState {
  status: "idle" | "loading" | "failed";
  selectedCompany?: Company;
  allowedCompanies: Company[];
  addNewCompany?: boolean;
  selectedCompanyId?: string;
  licenses?: CompanyLicense[];
}

const initialState: CompanyState = {
  status: "idle",
  allowedCompanies: [],
  licenses: [],
};

const setSelectedCompanyAndLicenses = (
  state: CompanyState,
  company?: Company,
) => {
  state.selectedCompany = company;
  state.licenses = company?.licenses ?? [];
};

const autoSelectCompanyIfSingle = (state: CompanyState) => {
  if (state.allowedCompanies.length === 1) {
    setSelectedCompanyAndLicenses(state, state.allowedCompanies[0]);
    return true;
  }

  return false;
};

const buildFormData = (formData: any, data: any, parentKey?: any) => {
  if (
    data &&
    typeof data === "object" &&
    !(data instanceof Date) &&
    !(data instanceof File)
  ) {
    Object.keys(data).forEach((key) => {
      buildFormData(
        formData,
        data[key],
        parentKey ? `${parentKey}[${key}]` : key,
      );
    });
  } else {
    const value = data == null || data === undefined ? null : data;

    if (!!data) {
      formData.append(parentKey, value);
    }
  }

  return formData;
};

export const fetchCompanyList = createAsyncThunk<Company[]>(
  "companies/fetch-all",
  async () => {
    const response = await http.get<Company[]>("/company", {
      params: {
        refreshToken: true,
      },
    });
    return response.data;
  },
);

export const saveNewCompany = createAsyncThunk<Company, Company>(
  "companies/new",
  async (vm: Company) => {
    const formData = buildFormData(new FormData(), vm);
    if (vm.settings?.reportLogo?.upload) {
      formData.append(
        "settings.reportLogo.upload",
        vm.settings?.reportLogo?.upload,
      );
    }

    const response = await http.post<Company>("/company", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        successToast: true,
      },
    });
    return response.data;
  },
);

export const editCompany = createAsyncThunk<Company, Company>(
  "companies/edit",
  async (vm: Company) => {
    const formData = buildFormData(new FormData(), vm);
    if (vm.settings?.reportLogo?.upload) {
      formData.append(
        "settings.reportLogo.upload",
        vm.settings?.reportLogo?.upload,
      );
    }

    const response = await http.put<Company>(`/company/${vm.id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        successToast: true,
      },
    });
    return response.data;
  },
);

export const deleteCompany = createAsyncThunk<null, number>(
  "companies/delete",
  async (id: number) => {
    const response = await http.delete(`/company/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  },
);

export const checkTokenForCompany = createAsyncThunk<AuthSession>(
  "companies/selected",
  async () => {
    const response = await fetchAuthSession();
    return response;
  },
);

export const getCompany = createAsyncThunk<
  Company | undefined,
  number | undefined
>("companies/get", async (id?: number) => {
  if (!id) {
    await http.get(`/auth/logout`).then(async () => {
      return undefined;
    });
  } else {
    const response = await http.get<Company>(`/company/${id}`, {
      params: {
        refreshToken: true,
      },
    });
    return response.data;
  }
});

export const startTrial = createAsyncThunk<
  Company,
  { id: number; appId?: number }
>("companies/license", async ({ id, appId }) => {
  let url = `/company/${id}/licenses`;
  if (appId) url += `?appId=${appId}`;

  const response = await http.post<Company>(url);
  return response.data;
});

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    setNewCompany(state, action) {
      state.addNewCompany = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyList.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCompanyList.fulfilled, (state, action) => {
        state.status = "idle";

        const companies = action.payload;
        state.allowedCompanies = companies;

        if (autoSelectCompanyIfSingle(state)) {
          return;
        }

        if (companies.length > 0) {
          const selected = companies.find(
            (x) => x.tenantId === state.selectedCompanyId,
          );

          if (selected) {
            setSelectedCompanyAndLicenses(state, selected);
          }
        }
      })
      .addCase(fetchCompanyList.rejected, (state) => {
        state.status = "failed";
        state.allowedCompanies = [];
        setSelectedCompanyAndLicenses(state, undefined);
      })
      .addCase(saveNewCompany.pending, (state) => {
        state.status = "loading";
      })
      .addCase(saveNewCompany.fulfilled, (state, action) => {
        state.status = "idle";
        state.allowedCompanies?.push(action.payload);
        setSelectedCompanyAndLicenses(state, action.payload);
        state.addNewCompany = false;
      })
      .addCase(saveNewCompany.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editCompany.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editCompany.fulfilled, (state, action) => {
        state.status = "idle";
        setSelectedCompanyAndLicenses(state, action.payload);
        state.addNewCompany = false;

        let existingCompany = state.allowedCompanies.findIndex(
          (x) => x.id === action.payload.id,
        );

        if (existingCompany !== -1) {
          state.allowedCompanies[existingCompany] = action.payload;
        }
      })
      .addCase(editCompany.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteCompany.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteCompany.fulfilled, (state) => {
        state.status = "idle";
        setSelectedCompanyAndLicenses(state, undefined);
        state.addNewCompany = false;
      })
      .addCase(deleteCompany.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getCompany.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getCompany.fulfilled, (state, action) => {
        state.status = "idle";
        setSelectedCompanyAndLicenses(state, action.payload);

        if (action.payload) {
          state.addNewCompany = false;
          let existingCompany = state.allowedCompanies.findIndex(
            (x) => x.id === action.payload?.id,
          );

          if (existingCompany !== -1) {
            state.allowedCompanies[existingCompany] = action.payload;
          }
        }
      })
      .addCase(getCompany.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(startTrial.pending, (state) => {
        state.status = "loading";
      })
      .addCase(startTrial.fulfilled, (state, action) => {
        state.status = "idle";
        setSelectedCompanyAndLicenses(state, action.payload);

        if (action.payload) {
          state.addNewCompany = false;
          let existingCompany = state.allowedCompanies.findIndex(
            (x) => x.id === action.payload?.id,
          );

          if (existingCompany !== -1) {
            state.allowedCompanies[existingCompany] = action.payload;
          }
        }
      })
      .addCase(startTrial.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(checkTokenForCompany.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkTokenForCompany.fulfilled, (state, action) => {
        state.status = "idle";

        const session = action.payload;
        const response = session.tokens?.idToken?.payload;
        const selectedTenant = response && response["custom:active_tenant_id"];

        if (!!selectedTenant) {
          state.addNewCompany = false;
          state.selectedCompanyId = selectedTenant?.toString();
        }
      })
      .addCase(checkTokenForCompany.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const { setNewCompany } = companySlice.actions;

export default companySlice.reducer;
